import { API_BASE_URL } from '../config/api';

function getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('access_token');
    return {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
    };
}

// ===== TYPES =====

export type InvoiceStatus = 'open' | 'due' | 'paid' | 'collections' | 'uncollectible';

export interface Invoice {
    id: string;
    encounter_id: string;
    pet_id: string;
    owner_id: string;
    practice_id: string;
    invoice_number: string;
    status: InvoiceStatus;
    subtotal: number;
    discount_total: number;
    tax_amount: number;
    total_amount: number;
    amount_paid: number;
    balance_due: number;
    encounter_date: string;
    due_date?: string;
    paid_at?: string;
    notes?: string;
    created_at: string;
    updated_at: string;
}

export interface PetBillingSummary {
    pet_id: string;
    current_balance: number;
    credit_balance: number;
    lifetime_value: number;
    trailing_year_value: number;
    invoice_count: number;
    unpaid_count: number;
}

export interface OwnerBillingSummary {
    owner_id: string;
    current_balance: number;
    credit_balance: number;
    lifetime_value: number;
    trailing_year_value: number;
    invoice_count: number;
}

// ===== API FUNCTIONS =====

/**
 * Get all invoices for a pet
 */
export const getPetInvoices = async (petId: string): Promise<Invoice[]> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/pets/${petId}/invoices`, {
        headers: getAuthHeaders()
    });

    if (!response.ok) throw new Error('Failed to fetch invoices');
    return response.json();
};

/**
 * Get billing summary for a pet (Balance, Credit, LTV, Trailing Year)
 */
export const getPetBillingSummary = async (petId: string): Promise<PetBillingSummary> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/pets/${petId}/billing-summary`, {
        headers: getAuthHeaders()
    });

    if (!response.ok) throw new Error('Failed to fetch billing summary');
    return response.json();
};

/**
 * Get a single invoice by ID
 */
export const getInvoice = async (invoiceId: string): Promise<Invoice> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/invoices/${invoiceId}`, {
        headers: getAuthHeaders()
    });

    if (!response.ok) throw new Error('Failed to fetch invoice');
    return response.json();
};

/**
 * Update invoice status
 */
export const updateInvoiceStatus = async (invoiceId: string, status: InvoiceStatus): Promise<Invoice> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/invoices/${invoiceId}/status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status })
    });

    if (!response.ok) throw new Error('Failed to update invoice status');
    return response.json();
};

/**
 * Record a payment on an invoice
 */
export const recordInvoicePayment = async (invoiceId: string, amount: number): Promise<Invoice> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/invoices/${invoiceId}/payment`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ amount })
    });

    if (!response.ok) throw new Error('Failed to record payment');
    return response.json();
};

/**
 * Update pet owner credit balance
 */
export const updateOwnerCredit = async (petId: string, creditBalance: number): Promise<{ credit_balance: number }> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/pets/${petId}/credit`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ credit_balance: creditBalance })
    });

    if (!response.ok) throw new Error('Failed to update credit balance');
    return response.json();
};

/**
 * Get billing summary for a pet owner
 */
export const getOwnerBillingSummary = async (ownerId: string): Promise<OwnerBillingSummary> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/owners/${ownerId}/billing-summary`, {
        headers: getAuthHeaders()
    });

    if (!response.ok) throw new Error('Failed to fetch owner billing summary');
    return response.json();
};

// ===== UTILITY FUNCTIONS =====

/**
 * Format currency for display
 */
export const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(amount);
};

/**
 * Get status badge color
 */
export const getStatusColor = (status: InvoiceStatus): string => {
    switch (status) {
        case 'open':
            return 'bg-blue-100 text-blue-800';
        case 'due':
            return 'bg-yellow-100 text-yellow-800';
        case 'paid':
            return 'bg-green-100 text-green-800';
        case 'collections':
            return 'bg-red-100 text-red-800';
        case 'uncollectible':
            return 'bg-gray-100 text-gray-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
};

/**
 * Get human-readable status label
 */
export const getStatusLabel = (status: InvoiceStatus): string => {
    switch (status) {
        case 'open':
            return 'Open';
        case 'due':
            return 'Due';
        case 'paid':
            return 'Paid';
        case 'collections':
            return 'Collections';
        case 'uncollectible':
            return 'Uncollectible';
        default:
            return status;
    }
};

